import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HelpCircle,
  X,
  Search,
  BookOpen,
  Play,
  Keyboard,
  Lightbulb,
  Bot,
} from 'lucide-react';
import { usePulseBot } from '../../context/PulseBotContext';

// ---------------------------------------------------------------------------
// Page-contextual help content
// ---------------------------------------------------------------------------

interface PageHelp {
  title: string;
  tips: string[];
}

const PAGE_HELP: Record<string, PageHelp> = {
  '/dashboard': {
    title: 'Dashboard',
    tips: [
      'The dashboard shows your company KPI overview.',
      'Stat cards at the top show live counts from the database.',
      'Charts update whenever you or your team add new KPIs.',
      'Use the theme switcher (gear icon) to change background colours.',
    ],
  },
  '/performance/goals': {
    title: 'Company Goals',
    tips: [
      'Create OKR-style goals for the whole company.',
      'Assign owners and track progress with status updates.',
      'Link KPIs to goals to see how metrics drive objectives.',
    ],
  },
  '/performance/kpis': {
    title: 'KPI Tracker',
    tips: [
      'Add KPIs for each department with targets.',
      'Status auto-calculates: ≥80% = On Track, 50–79% = At Risk, <50% = Off Track.',
      'Click a row to edit or view history.',
    ],
  },
  '/hr/my-leave': {
    title: 'My Leave',
    tips: [
      'Submit leave requests here.',
      'Your balances update automatically when leave is approved.',
      'You can cancel pending requests before they are approved.',
    ],
  },
  '/finance/expenses': {
    title: 'Expenses',
    tips: [
      'Submit expense claims for reimbursement.',
      'Attach your budget code when submitting.',
      'Track status from Draft → Submitted → Approved → Paid.',
    ],
  },
  '/crm/dashboard': {
    title: 'CRM Dashboard',
    tips: [
      'Overview of your sales pipeline and activity.',
      'Win rate calculates from closed deals in the last 90 days.',
      'Closing soon shows deals due in the next 30 days.',
    ],
  },
  '/crm/deals': {
    title: 'CRM Deals',
    tips: [
      'Drag cards between columns to update deal stage.',
      'Click a card to open the detail panel and log activities.',
      'Set probability to track weighted pipeline value.',
    ],
  },
  '/crm/reports': {
    title: 'Sales Reports',
    tips: [
      'Use the date range buttons to scope the report window.',
      'Weighted forecast multiplies each open deal value by its probability.',
      'Win rate counts closed-won as a share of all closed deals.',
      'Click "CSV" to download the underlying deals for the selected range.',
    ],
  },
  '/analytics/overview': {
    title: 'Analytics Overview',
    tips: [
      'Change the date range to see different periods.',
      'Export CSV downloads the department comparison table.',
      'Charts pull live data from all modules simultaneously.',
    ],
  },
};

const FALLBACK_HELP: PageHelp = {
  title: 'Help',
  tips: [
    'Use the sidebar to navigate between modules.',
    'Press Ctrl+K to open the command palette.',
    'Click any row to open a detail panel.',
  ],
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface HelpWidgetProps {
  onShowShortcuts: () => void;
  onReplayTour: () => void;
}

// ---------------------------------------------------------------------------
// PageHelpDrawer — slide-in panel from right
// ---------------------------------------------------------------------------

interface PageHelpDrawerProps {
  pageHelp: PageHelp;
  onClose: () => void;
}

function PageHelpDrawer({ pageHelp, onClose }: PageHelpDrawerProps) {
  return (
    <div
      className="fixed inset-y-0 right-0 w-80 z-50 flex flex-col"
      style={{
        background: 'rgba(255,255,255,0.10)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.15)',
        color: '#fff',
      }}
    >
      {/* Drawer header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <h2 className="text-white font-semibold text-sm">{pageHelp.title}</h2>
        <button
          onClick={onClose}
          className="text-white/60 hover:text-white transition-colors"
          aria-label="Close page guide"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tips list */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {pageHelp.tips.map((tip, index) => (
          <div
            key={index}
            className="flex items-start gap-3 p-3 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.06)' }}
          >
            <Lightbulb
              size={15}
              className="text-teal-400 mt-0.5 shrink-0"
              aria-label="Tip"
            />
            <p className="text-white/85 text-sm leading-relaxed">{tip}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HelpWidget
// ---------------------------------------------------------------------------

export default function HelpWidget({ onShowShortcuts, onReplayTour }: HelpWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showPageHelp, setShowPageHelp] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { open: openBot } = usePulseBot();

  const currentPageHelp = PAGE_HELP[location.pathname] ?? FALLBACK_HELP;

  function handleAskBot() {
    setIsOpen(false);
    openBot();
  }

  function handleShowPageHelp() {
    setIsOpen(false);
    setShowPageHelp(true);
  }

  function handleReplayTour() {
    setIsOpen(false);
    onReplayTour();
  }

  function handleShowShortcuts() {
    setIsOpen(false);
    onShowShortcuts();
  }

  function handleNavigateKB() {
    setIsOpen(false);
    navigate('/support/knowledge-base');
  }

  return (
    <>
      {/* Page help drawer */}
      {showPageHelp && (
        <PageHelpDrawer
          pageHelp={currentPageHelp}
          onClose={() => setShowPageHelp(false)}
        />
      )}

      {/* Floating widget.
          `data-help-dock` is a hook for a SCREEN that knows something sits in this corner:
          Mail's contact panel ends in a note this button would cover, so while the panel is
          open Mail moves the dock aside (`styles/mail.css`, Lis 2026-10-18). Nothing here
          reads it; without that screen open, the button is exactly where it always was. */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end" data-help-dock="">
        {/* Panel — shown above the button when open */}
        {isOpen && (
          <div
            className="p-pop absolute bottom-16 right-0 w-64 overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.10)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '12px',
              color: '#fff',
            }}
          >
            {/* Panel header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="text-white text-sm font-semibold">Help &amp; Assistant</span>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/60 hover:text-white transition-colors"
                aria-label="Close help panel"
              >
                <X size={14} />
              </button>
            </div>

            {/* Menu items */}
            <ul role="menu">
              {/* Assistant — opens the PulseBot chat panel (merged from the
                  former separate bot FAB). */}
              <li role="none">
                <button
                  role="menuitem"
                  onClick={handleAskBot}
                  className="w-full px-4 py-3 hover:bg-white/10 cursor-pointer flex items-center gap-3 text-left transition-colors"
                >
                  <Bot size={15} className="text-teal-300 shrink-0" aria-label="Assistant" />
                  <span className="text-white/85 text-sm">Ask PulseBot</span>
                </button>
              </li>
              <li role="none">
                <button
                  role="menuitem"
                  onClick={handleNavigateKB}
                  className="w-full px-4 py-3 hover:bg-white/10 cursor-pointer flex items-center gap-3 text-left transition-colors"
                >
                  <Search size={15} className="text-white/60 shrink-0" aria-label="Search" />
                  <span className="text-white/85 text-sm">Search Knowledge Base</span>
                </button>
              </li>
              <li role="none">
                <button
                  role="menuitem"
                  onClick={handleShowPageHelp}
                  className="w-full px-4 py-3 hover:bg-white/10 cursor-pointer flex items-center gap-3 text-left transition-colors"
                >
                  <BookOpen size={15} className="text-white/60 shrink-0" aria-label="Page guide" />
                  <span className="text-white/85 text-sm">Page Guide</span>
                </button>
              </li>
              <li role="none">
                <button
                  role="menuitem"
                  onClick={handleReplayTour}
                  className="w-full px-4 py-3 hover:bg-white/10 cursor-pointer flex items-center gap-3 text-left transition-colors"
                >
                  <Play size={15} className="text-white/60 shrink-0" aria-label="Replay tour" />
                  <span className="text-white/85 text-sm">Replay Tour</span>
                </button>
              </li>
              <li role="none">
                <button
                  role="menuitem"
                  onClick={handleShowShortcuts}
                  className="w-full px-4 py-3 hover:bg-white/10 cursor-pointer flex items-center gap-3 text-left transition-colors"
                >
                  <Keyboard size={15} className="text-white/60 shrink-0" aria-label="Keyboard shortcuts" />
                  <span className="text-white/85 text-sm">Keyboard Shortcuts</span>
                </button>
              </li>
            </ul>

            {/* Panel footer */}
            <div className="px-4 py-2 border-t border-white/10">
              <span className="text-white/40 text-xs">CBOps Help Center</span>
            </div>
          </div>
        )}

        {/* Trigger button */}
        <button
          data-tour="help-button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-12 h-12 rounded-full bg-teal-500 hover:bg-teal-400 shadow-lg flex items-center justify-center text-white transition-all"
          aria-label={isOpen ? 'Close help' : 'Open help'}
          aria-expanded={isOpen}
        >
          <HelpCircle size={22} aria-label="Help" />
        </button>
      </div>
    </>
  );
}
